
import { Container } from "@/components/layout/Container";
import { SEO } from "@/components/shared/SEO";
import { motion } from "framer-motion";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Skeleton } from "@/components/ui/skeleton";
import { Button } from "@/components/ui/button";
import { useQuery } from "@tanstack/react-query";
import { ChevronDown, ChevronUp } from "lucide-react";
import { useState } from "react";
import type { Work, WorkResponse } from "@shared/types";

// Works用のスケルトンコンポーネント
function WorkSkeleton() {
  return (
    <Card className="h-full">
      <CardHeader>
        <div className="flex items-center justify-between mb-2">
          <Skeleton className="h-4 w-20" />
          <Skeleton className="h-4 w-12" />
        </div>
        <Skeleton className="h-6 w-48" />
      </CardHeader>
      <CardContent>
        <Skeleton className="h-16 w-full mb-4" />
        <div className="flex flex-wrap gap-2">
          <Skeleton className="h-6 w-16" />
          <Skeleton className="h-6 w-20" />
          <Skeleton className="h-6 w-14" />
        </div>
      </CardContent>
    </Card>
  );
}

// 展開可能な説明文コンポーネント
function ExpandableDescription({ description, workId }: { description: string; workId: string }) {
  const [isExpanded, setIsExpanded] = useState(false);
  const isLongText = description.length > 120; // 120文字以上で「もっと見る」ボタンを表示

  return (
    <div className="text-muted-foreground mb-4">
      <p className={isLongText && !isExpanded ? "line-clamp-3" : ""}>
        {description}
      </p>
      {isLongText && (
        <Button
          variant="ghost"
          size="sm"
          onClick={() => setIsExpanded(!isExpanded)}
          className="mt-2 p-0 h-auto text-sm hover:bg-transparent hover:text-foreground transition-colors"
        >
          {isExpanded ? (
            <>
              <ChevronUp className="w-3 h-3 mr-1" />
              閉じる
            </>
          ) : (
            <>
              <ChevronDown className="w-3 h-3 mr-1" />
              もっと見る
            </>
          )}
        </Button>
      )}
    </div>
  );
}

export default function Works() {
  // バックエンドAPIからWorksデータを取得
  const { data: worksData, isLoading, error } = useQuery<WorkResponse>({
    queryKey: ['/api/works'],
  });

  return (
    <>
      <SEO
        title="Works"
        description="私が手がけたプロジェクトの一覧。Webアプリケーション、モバイルアプリ、バックエンドシステムなど様々な制作実績を紹介しています。"
        keywords="portfolio, web development, React, TypeScript, frontend development, mobile app, backend"
      />
      <Container className="py-16 md:py-24">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.5 }}
          className="max-w-5xl mx-auto text-center mb-12"
        >
          <h1 className="text-4xl font-bold tracking-tighter mb-4">Works</h1>
          <p className="text-lg text-muted-foreground">
            これまでに手がけたプロジェクトをご紹介します。
          </p>
        </motion.div>

        <div className="max-w-6xl mx-auto">
          {error && (
            <div className="text-center py-8">
              <p className="text-red-500 mb-4">
                作品の読み込み中にエラーが発生しました。
              </p>
              <p className="text-sm text-muted-foreground">
                しばらく時間をおいてから再度お試しください。
              </p>
            </div>
          )}

          {isLoading ? (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
              {Array.from({ length: 6 }).map((_, index) => (
                <WorkSkeleton key={index} />
              ))}
            </div>
          ) : worksData?.contents && worksData.contents.length > 0 ? (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
              {worksData.contents.map((work: Work, index: number) => (
                <motion.div
                  key={work.id}
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ duration: 0.5, delay: index * 0.1 }}
                >
                  <Card className="h-full hover:shadow-md transition-shadow duration-300">
                    <CardHeader>
                      <div className="flex items-center justify-between mb-2">
                        <span className="text-sm text-muted-foreground">
                          {work.category}
                        </span>
                        <span className="text-sm text-muted-foreground">
                          {work.year}
                        </span>
                      </div>
                      <CardTitle className="text-xl">{work.title}</CardTitle>
                    </CardHeader>
                    <CardContent>
                      <ExpandableDescription 
                        description={work.description}
                        workId={work.id}
                      />
                      
                      <div className="flex flex-wrap gap-2">
                        {work.tags && Array.isArray(work.tags) && work.tags.map((tag: any, tagIndex: number) => (
                          <span
                            key={tagIndex}
                            className="text-xs px-2 py-1 bg-muted rounded text-muted-foreground"
                          >
                            {typeof tag === 'string' ? tag : tag.tag || tag.fieldId || 'タグ'}
                          </span>
                        ))}
                      </div>
                    </CardContent>
                  </Card>
                </motion.div>
              ))}
            </div>
          ) : (
            <div className="text-center py-8">
              <p className="text-muted-foreground mb-4">
                現在表示できる作品がありません。
              </p>
              <p className="text-sm text-muted-foreground">
                作品が追加されるまでお待ちください。
              </p>
            </div>
          )}
        </div>
      </Container>
    </>
  );
}
